// Import model
import { User } from '../models/User';

// Import base class serializer
import { EntitySerializer } from './EntitySerializer';

// Import dependency types (models and enums used in properties)
import { Organization } from '../models/Organization';

// Import dependency serializers (for complex types)
import { OrganizationSerializer } from './OrganizationSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for User - handles JSON serialization and deserialization with type validation
 */
export class UserSerializer {
  /**
   * Deserializes a JSON string into a User instance
   */
  static deserialize(json: string): User {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a User instance
   */
  static fromObject(obj: any): User {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to User');
    }


    const instance: User = {
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      id: obj['_id'] as string | undefined,
      t: obj['_t'] as string,
      email: obj['email'] as string | undefined,
      phone: obj['phone'] as string | undefined,
      timeZone: obj['timeZone'] as string | undefined,
      organizationId: obj['organizationId'] as string | undefined,
      organizationIdName: obj['organizationId|Name'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      role: obj['role'] as string | undefined,
      organization: obj['organization'] ? OrganizationSerializer.fromObject(obj['organization']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of User instances
   */
  static deserializeArray(json: string): User[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a User instance to a JSON string
   */
  static serialize(instance: User): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a User instance to a plain object
   */
  static toObject(instance: User): any {
    return {
      'name': instance.name,
      'description': instance.description,
      '_id': instance.id,
      '_t': instance.t,
      'email': instance.email,
      'phone': instance.phone,
      'timeZone': instance.timeZone,
      'organizationId': instance.organizationId,
      'organizationId|Name': instance.organizationIdName,
      'isActive': instance.isActive,
      'role': instance.role,
      'organization': instance.organization ? OrganizationSerializer.toObject(instance.organization) : undefined,
    };
  }

  /**
   * Serializes an array of User instances to a JSON string
   */
  static serializeArray(instances: User[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
